import React from 'react';
import { Box, Flex, Grid, SimpleGrid, Skeleton, Text } from '@chakra-ui/react';
import { Archive, CheckCircle, Clock, Stack, TrendUp, User, Warning } from 'phosphor-react';
import {
  ESTATUTO_STATUS_LABELS,
  IEstatutoDashboard,
  IEstatutoDashboardSla,
} from '../../../../hooks/useEstatuto';

// ── KPI card (mockup #7): ícone colorido + número grande + rótulo/sub ────────────
interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  sub?: string;
  icon: React.ReactNode;
  color: string;
  bg: string;
  // Cartão de alerta: fundo/borda tingidos (atrasados vermelho, aguardando âmbar).
  cardBg?: string;
  cardBorder?: string;
  valueColor?: string;
  isLoading?: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({
  label,
  value,
  sub,
  icon,
  color,
  bg,
  cardBg = 'white',
  cardBorder = 'gray.100',
  valueColor = 'gray.900',
  isLoading,
}) => (
  <Box bg={cardBg} borderWidth="1px" borderColor={cardBorder} borderRadius="2xl" p={4}>
    <Flex align="center" gap={2.5}>
      <Flex
        align="center"
        justify="center"
        w="30px"
        h="30px"
        borderRadius="lg"
        bg={bg}
        color={color}
        flexShrink={0}
      >
        {icon}
      </Flex>
      <Text fontSize="12px" color="gray.500" fontWeight="600">
        {label}
      </Text>
    </Flex>
    {isLoading ? (
      <Skeleton height="28px" width="48px" mt={3} />
    ) : (
      <Text fontSize="28px" fontWeight="800" color={valueColor} lineHeight="1.1" mt={3}>
        {value}
      </Text>
    )}
    {sub && (
      <Text fontSize="11px" color="gray.400" mt={0.5}>
        {sub}
      </Text>
    )}
  </Box>
);

interface EstatutoKpiRowProps {
  dashboard?: IEstatutoDashboard;
  isLoading?: boolean;
}

/** Faixa compacta de KPIs (total / finalizados / arquivados) para o topo do quadro. */
export const EstatutoKpiRow: React.FC<EstatutoKpiRowProps> = ({ dashboard, isLoading }) => (
  <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3} mb={4}>
    <KpiCard
      label="Processos ativos"
      value={dashboard?.total ?? 0}
      icon={<Stack size={18} weight="duotone" />}
      color="#7C3AED"
      bg="#EDE9FE"
      isLoading={isLoading}
    />
    <KpiCard
      label="Finalizados"
      value={dashboard?.finalizados ?? 0}
      icon={<CheckCircle size={18} weight="duotone" />}
      color="#16A34A"
      bg="#DCFCE7"
      valueColor="#16A34A"
      isLoading={isLoading}
    />
    <KpiCard
      label="Arquivados"
      value={dashboard?.arquivados ?? 0}
      icon={<Archive size={18} weight="duotone" />}
      color="#64748B"
      bg="#F1F5F9"
      isLoading={isLoading}
    />
  </SimpleGrid>
);

interface ChartCardProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, subtitle, icon, children }) => (
  <Box bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="2xl" p={5}>
    <Flex align="center" gap={2.5} mb={subtitle ? 1 : 4}>
      {icon}
      <Text fontSize="15px" fontWeight="800" color="gray.800">
        {title}
      </Text>
    </Flex>
    {subtitle && (
      <Text fontSize="12px" color="gray.400" mb={4}>
        {subtitle}
      </Text>
    )}
    {children}
  </Box>
);

// ── Faixas do SLA: só apresentação. As contagens vêm agregadas do banco ──────────
// (GET /api/v1/estatuto/dashboard/{destino}); nada aqui é recalculado no cliente.
type SlaSeg = { key: keyof IEstatutoDashboardSla; label: string; desc: string; color: string };

const SLA_FAIXAS: SlaSeg[] = [
  { key: 'no_prazo', label: 'No prazo', desc: 'dentro do SLA', color: '#16A34A' },
  { key: 'em_risco', label: 'Em risco', desc: 'vence em ≤ 2 dias', color: '#F59E0B' },
  { key: 'atrasado', label: 'Atrasado', desc: 'SLA estourado', color: '#DC2626' },
];

// ── Donut SVG de SLA (como o mockup) ─────────────────────────────────────────────
const SlaDonut: React.FC<{ sla: IEstatutoDashboardSla; total: number }> = ({ sla, total }) => {
  const R = 80;
  const C = 2 * Math.PI * R;
  let cum = 0;
  const segs = SLA_FAIXAS.map(f => {
    const s = { ...f, count: sla[f.key] };
    const frac = total ? s.count / total : 0;
    const dashLen = frac * C;
    const seg = {
      ...s,
      dash: `${dashLen.toFixed(1)} ${(C - dashLen).toFixed(1)}`,
      offset: (-cum * C).toFixed(1),
      pct: Math.round(frac * 100),
    };
    cum += frac;
    return seg;
  });
  return (
    <Flex align="center" gap={7} py={1} wrap="wrap">
      <Box position="relative" flex="0 0 180px" w="180px" h="180px">
        <svg viewBox="0 0 200 200" style={{ width: 180, height: 180, transform: 'rotate(-90deg)' }}>
          <circle cx="100" cy="100" r={R} fill="none" stroke="#F1F5F9" strokeWidth="26" />
          {segs.map(s => (
            <circle
              key={s.label}
              cx="100"
              cy="100"
              r={R}
              fill="none"
              stroke={s.color}
              strokeWidth="26"
              strokeDasharray={s.dash}
              strokeDashoffset={s.offset}
            />
          ))}
        </svg>
        <Flex position="absolute" inset={0} direction="column" align="center" justify="center">
          <Text fontSize="34px" fontWeight="800" lineHeight="1">
            {total}
          </Text>
          <Text fontSize="12px" color="gray.400" fontWeight="600">
            ativos
          </Text>
        </Flex>
      </Box>
      <Flex flex={1} direction="column" gap={3.5} minW="150px">
        {segs.map(s => (
          <Flex key={s.label} align="center" gap={3}>
            <Box w="12px" h="12px" borderRadius="4px" bg={s.color} flexShrink={0} />
            <Box flex={1}>
              <Text fontSize="13.5px" fontWeight="700" color="gray.700">
                {s.label}
              </Text>
              <Text fontSize="11px" color="gray.400">
                {s.desc}
              </Text>
            </Box>
            <Box textAlign="right">
              <Text fontSize="16px" fontWeight="800" color={s.color}>
                {s.count}
              </Text>
              <Text fontSize="11px" color="gray.400" fontWeight="600">
                {s.pct}%
              </Text>
            </Box>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

// ── Distribuição por etapa (barras horizontais, gargalo destacado) ───────────────
const DistribuicaoEtapas: React.FC<{
  porEtapa: { etapa: string; total: number }[];
}> = ({ porEtapa }) => {
  const max = Math.max(1, ...porEtapa.map(e => e.total));
  const top = porEtapa.reduce((a, b) => (b.total > a.total ? b : a), porEtapa[0]);
  return (
    <Flex direction="column" gap={4}>
      {porEtapa.map(e => {
        // Etapa do cliente em âmbar (é onde o processo "trava" no externo).
        const isCliente = /cliente/i.test(e.etapa);
        const isGargalo = e === top && e.total > 0;
        const cor = isCliente ? '#F59E0B' : '#1877C8';
        return (
          <Box key={e.etapa}>
            <Flex justify="space-between" align="center" mb={1.5}>
              <Flex align="center" gap={2}>
                <Text fontSize="13px" fontWeight="600" color="gray.700">
                  {e.etapa}
                </Text>
                {isGargalo && (
                  <Text
                    fontSize="10px"
                    fontWeight="800"
                    color="#B45309"
                    bg="#FEF3C7"
                    px={2}
                    py={0.5}
                    borderRadius="full"
                  >
                    GARGALO
                  </Text>
                )}
              </Flex>
              <Text fontSize="13px" fontWeight="800" color="gray.900">
                {e.total}
              </Text>
            </Flex>
            <Box h="12px" bg="gray.100" borderRadius="full" overflow="hidden">
              <Box
                h="100%"
                w={`${Math.round((e.total / max) * 100)}%`}
                bg={cor}
                borderRadius="full"
              />
            </Box>
          </Box>
        );
      })}
    </Flex>
  );
};

interface EstatutoDashboardProps {
  dashboard?: IEstatutoDashboard;
  isLoading?: boolean;
}

/** Dashboard operacional (#7): KPIs + distribuição por etapa + saúde do SLA + atenção.
 *  Componente de apresentação puro — todo número vem agregado do backend. */
export const EstatutoDashboard: React.FC<EstatutoDashboardProps> = ({ dashboard, isLoading }) => {
  const porEtapa = dashboard?.por_etapa ?? [];
  const sla: IEstatutoDashboardSla = dashboard?.sla ?? {
    no_prazo: 0,
    em_risco: 0,
    atrasado: 0,
  };
  const atencao = dashboard?.atencao ?? [];
  const totalAtivos = dashboard?.total ?? 0;
  const slaTotal = sla.no_prazo + sla.em_risco + sla.atrasado;

  const renderEmpty = (msg = 'Sem dados.') => (
    <Flex align="center" justify="center" h="240px">
      <Text fontSize="sm" color="gray.500">
        {msg}
      </Text>
    </Flex>
  );

  // Helpers de render (evitam ternário aninhado no JSX — no-nested-ternary).
  const renderDistribuicao = () => {
    if (isLoading) return <Skeleton height="240px" borderRadius="md" />;
    if (porEtapa.length === 0) return renderEmpty('Sem dados por etapa.');
    return <DistribuicaoEtapas porEtapa={porEtapa} />;
  };

  const renderSla = () => {
    if (isLoading) return <Skeleton height="200px" borderRadius="md" />;
    if (slaTotal === 0) return renderEmpty('Nenhum processo ativo tem prazo definido.');
    return <SlaDonut sla={sla} total={slaTotal} />;
  };

  const renderAtencao = () => {
    if (isLoading) return <Skeleton height="140px" borderRadius="md" />;
    if (atencao.length === 0) {
      return (
        <Text fontSize="sm" color="gray.400" py={4}>
          Nenhum processo com SLA estourado. 🎉
        </Text>
      );
    }
    return (
      <Box>
        <Grid
          templateColumns="1fr 200px 92px"
          gap={3}
          px={1}
          pb={2}
          borderBottomWidth="1px"
          borderColor="gray.100"
        >
          <Text fontSize="11px" fontWeight="700" color="gray.400" letterSpacing="0.04em">
            ESTATUTO
          </Text>
          <Text fontSize="11px" fontWeight="700" color="gray.400" letterSpacing="0.04em">
            ETAPA ATUAL
          </Text>
          <Text
            fontSize="11px"
            fontWeight="700"
            color="gray.400"
            letterSpacing="0.04em"
            textAlign="right"
          >
            ATRASO
          </Text>
        </Grid>
        {atencao.map(a => (
          <Grid
            key={a.id}
            templateColumns="1fr 200px 92px"
            gap={3}
            alignItems="center"
            px={1}
            py={3}
            borderBottomWidth="1px"
            borderColor="gray.50"
          >
            <Text fontSize="13px" fontWeight="600" color="gray.700" noOfLines={1}>
              {a.titulo}
            </Text>
            <Text fontSize="11.5px" color="gray.500" fontWeight="600" noOfLines={1}>
              {ESTATUTO_STATUS_LABELS[a.status] ?? a.status}
            </Text>
            <Box textAlign="right">
              <Text
                as="span"
                display="inline-block"
                fontSize="12px"
                fontWeight="800"
                px={2.5}
                py={1}
                borderRadius="full"
                bg={a.dias_atraso >= 7 ? '#FEE2E2' : '#FEF3C7'}
                color={a.dias_atraso >= 7 ? '#DC2626' : '#B45309'}
              >
                +{a.dias_atraso}d
              </Text>
            </Box>
          </Grid>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      {/* ── Faixa de 6 KPIs ── */}
      <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={3.5} mb={5}>
        <KpiCard
          label="Total ativos"
          value={totalAtivos}
          sub="em andamento"
          icon={<Stack size={16} weight="duotone" />}
          color="#1670BE"
          bg="#E7F1FB"
          isLoading={isLoading}
        />
        <KpiCard
          label="Finalizados no mês"
          value={dashboard?.finalizados_mes ?? 0}
          sub="assinados este mês"
          icon={<CheckCircle size={16} weight="duotone" />}
          color="#16A34A"
          bg="#DCFCE7"
          valueColor="#16A34A"
          isLoading={isLoading}
        />
        <KpiCard
          label="Taxa de conclusão"
          value={`${dashboard?.taxa_conclusao ?? 0}%`}
          sub="finalizados / total"
          icon={<TrendUp size={16} weight="duotone" />}
          color="#1670BE"
          bg="#E7F1FB"
          isLoading={isLoading}
        />
        <KpiCard
          label="Atrasados (SLA)"
          value={dashboard?.atrasados ?? 0}
          sub="SLA estourado"
          icon={<Warning size={16} weight="duotone" />}
          color="#DC2626"
          bg="#FEE2E2"
          cardBg="#FEF2F2"
          cardBorder="#FBD0D0"
          valueColor="#DC2626"
          isLoading={isLoading}
        />
        <KpiCard
          label="Aguardando cliente"
          value={dashboard?.aguardando_cliente ?? 0}
          sub="ação externa pendente"
          icon={<User size={16} weight="duotone" />}
          color="#F59E0B"
          bg="#FEF3C7"
          cardBg="#FFFBEB"
          cardBorder="#FCE7B0"
          valueColor="#D97706"
          isLoading={isLoading}
        />
        <KpiCard
          label="Tempo médio"
          value={
            dashboard?.tempo_medio_dias == null ? (
              '—'
            ) : (
              <>
                {dashboard.tempo_medio_dias}
                <Text as="span" fontSize="15px" fontWeight="700" color="gray.400">
                  {' '}
                  dias
                </Text>
              </>
            )
          }
          sub="criação → assinatura"
          icon={<Clock size={16} weight="duotone" />}
          color="#475569"
          bg="#EEF2F6"
          isLoading={isLoading}
        />
      </SimpleGrid>

      {/* ── Distribuição por etapa + Saúde do SLA ── */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1.25fr' }} gap={4} mb={4}>
        <ChartCard
          title="Distribuição por etapa"
          subtitle="Processos ativos por coluna do Kanban — identificação de gargalos"
        >
          {renderDistribuicao()}
        </ChartCard>

        <ChartCard
          title="Saúde do SLA"
          subtitle={`Situação de prazo dos ${slaTotal} processos com data`}
        >
          {renderSla()}
        </ChartCard>
      </Grid>

      {/* ── Atenção necessária (processos mais atrasados) ── */}
      <ChartCard
        title="Atenção necessária"
        subtitle="Processos com maior atraso de SLA"
        icon={<Warning size={18} color="#DC2626" weight="fill" />}
      >
        {renderAtencao()}
      </ChartCard>
    </Box>
  );
};
